import { type ReadyGateVerdict, evaluateReadyGate } from './ready-gate';
import type { VariantSetDocument } from './types';

import type { ConceptStatus } from '@/lib/briefs/concept-contract';

/**
 * `Idea / In design / Ready / Live` DOES NOT EXIST IN THE DATABASE — zero grep
 * hits, and no column was added for it. It is a DISPLAY MAPPING over the stored
 * `project_tasks.status` CHECK constraint
 * (`todo|drafting|review|approved|failed|archived` for a creative-concept).
 *
 * `Live` is DERIVED and can never be hand-set: it is true when the concept has a
 * published `social_posts` row or an exported linked `ad_units` row. A
 * manually-settable "Live" is a lie waiting to happen (spec §6.9).
 */

export type ConceptDisplayStatus =
  'idea' | 'in-design' | 'in-review' | 'ready' | 'live' | 'failed' | 'archived';

export const CONCEPT_DISPLAY_LABEL: Record<ConceptDisplayStatus, string> = {
  idea: 'Idea',
  'in-design': 'In design',
  'in-review': 'In review',
  ready: 'Ready',
  live: 'Live',
  failed: 'Failed',
  archived: 'Archived',
};

const STORED_TO_DISPLAY: Record<ConceptStatus, ConceptDisplayStatus> = {
  todo: 'idea',
  drafting: 'in-design',
  review: 'in-review',
  approved: 'ready',
  failed: 'failed',
  archived: 'archived',
};

/** Evidence that the concept actually shipped. Computed, never stored. */
export interface LiveEvidence {
  publishedPostCount: number;
  exportedAdUnitCount: number;
}

export function isLive(evidence: LiveEvidence | undefined): boolean {
  if (!evidence) return false;
  return evidence.publishedPostCount > 0 || evidence.exportedAdUnitCount > 0;
}

/**
 * `Live` outranks `Ready` but never `Archived` — an archived concept is terminal
 * in the database (`prevent_archived_brief_concept_update`), so showing it as
 * Live would contradict the row.
 */
export function toDisplayStatus(
  stored: ConceptStatus,
  evidence?: LiveEvidence,
): ConceptDisplayStatus {
  const mapped = STORED_TO_DISPLAY[stored];
  if (mapped === 'archived' || mapped === 'failed') return mapped;
  return isLive(evidence) ? 'live' : mapped;
}

/** The display values a user is allowed to pick. `Live` is deliberately absent. */
export const SETTABLE_DISPLAY_STATUSES: readonly ConceptDisplayStatus[] = [
  'idea',
  'in-design',
  'in-review',
  'ready',
] as const;

export function isSettableDisplayStatus(value: ConceptDisplayStatus): boolean {
  return SETTABLE_DISPLAY_STATUSES.includes(value);
}

/** The stored status a settable display value writes. `Live` returns null. */
export function toStoredStatus(display: ConceptDisplayStatus): ConceptStatus | null {
  switch (display) {
    case 'idea':
      return 'todo';
    case 'in-design':
      return 'drafting';
    case 'in-review':
      return 'review';
    case 'ready':
      return 'approved';
    case 'failed':
      return 'failed';
    case 'archived':
      return 'archived';
    case 'live':
      return null;
  }
}

export interface StatusTransition {
  allowed: boolean;
  storedStatus: ConceptStatus | null;
  /** Populated when refused. Names the exact blocker for the tooltip. */
  verdict: ReadyGateVerdict | null;
  reason: string | null;
}

/**
 * The one place a status change is decided. `Ready` runs the gate; `Live` is
 * refused outright. Every other transition is a plain write.
 */
export function evaluateStatusTransition(
  target: ConceptDisplayStatus,
  doc: VariantSetDocument | null,
): StatusTransition {
  if (target === 'live') {
    return {
      allowed: false,
      storedStatus: null,
      verdict: null,
      reason:
        'Live is derived from published posts and exported ad units. It cannot be set by hand.',
    };
  }
  if (target === 'ready') {
    const verdict = evaluateReadyGate(doc);
    return {
      allowed: verdict.ok,
      storedStatus: verdict.ok ? 'approved' : null,
      verdict,
      reason: verdict.ok ? null : verdict.tooltip,
    };
  }
  return {
    allowed: true,
    storedStatus: toStoredStatus(target),
    verdict: null,
    reason: null,
  };
}
